<script setup>
import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import XlButton from '../../xl/XlButton.vue';
import XlModal from '../../xl/XlModal.vue';
import ManageAccessModal from '../../resource-access/ManageAccessModal.vue';
import useHubBrokers from '../../../composables/useHubBrokers';
import useHubSegments from '../../../composables/useHubSegments';
import { accessSummaryLine, bookFor, ownBookOf } from '../../../composables/useResourceAccess';
import { useAuthStore } from '../../../stores/auth';
import { getFileErrorMessage } from '../../../helpers/helper';

/**
 * Adding files and saying who can open them, in one go. The upload is the
 * existing multi-file POST; the audience is then written to the returned ids
 * through the bulk route, so nothing is ever created unaimed. If that second
 * write fails the files still exist — with the default audience — and the
 * modal says so instead of pretending.
 */
const props = defineProps({
  members: { type: Array, default: () => [] },
  // Bytes still free in the hub's storage; null when unknown.
  capacityLeftBytes: { type: Number, default: null },
});
const emit = defineEmits(['close', 'uploaded']);

const ACCEPT = 'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/jpeg,image/png,image/gif,text/plain,application/pdf';

const everyone = () => ({ all_participants: true, member_ids: [], segment_ids: [], broker_ids: [] });
const isEveryone = (access) => !access || access.all_participants;

const files = ref([]);
const access = ref(everyone());
const pickingAudience = ref(false);
const dragging = ref(false);
const busy = ref(false);
const progress = ref(0);
const error = ref('');
const notice = ref('');

const { segments, loadHubSegments } = useHubSegments();
onMounted(loadHubSegments);
const { loadHubBrokers } = useHubBrokers();
watch(() => access.value.broker_ids?.length, (count) => { if (count) loadHubBrokers(); }, { immediate: true });

const authStore = useAuthStore();
const summary = computed(() => accessSummaryLine(access.value, props.members, segments.value, { book: bookFor(null, authStore.userData), me: ownBookOf(authStore.userData) }));
const totalBytes = computed(() => files.value.reduce((sum, f) => sum + (f.size || 0), 0));
const stagedName = computed(() => (files.value.length === 1 ? files.value[0].name : `${files.value.length} files`));

const formatBytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const addFiles = (list) => {
  const incoming = Array.from(list || []);
  if (!incoming.length) return;
  error.value = '';
  const known = (f) => files.value.some((g) => g.name === f.name && g.size === f.size);
  files.value = [...files.value, ...incoming.filter((f) => !known(f))];
};

const onPick = (event) => {
  addFiles(event.target.files);
  event.target.value = null;
};

const onDrop = (event) => {
  dragging.value = false;
  addFiles(event.dataTransfer?.files);
};

const remove = (index) => files.value.splice(index, 1);

const onAudience = (chosen) => {
  access.value = chosen;
  pickingAudience.value = false;
};

const firstMessage = (message) => {
  if (typeof message === 'string') return message;
  if (message && typeof message === 'object') return Object.values(message).flat()[0] || '';
  return '';
};

const messageFrom = (e, fallback) => {
  if (e?.response?.status === 413) return 'Maximum capacity exceeded';
  return firstMessage(e?.response?.data?.message) || fallback;
};

const problemWithFiles = () => {
  const problem = getFileErrorMessage(files.value);
  if (problem) return problem;
  if (props.capacityLeftBytes !== null && totalBytes.value > props.capacityLeftBytes) return 'Not enough storage left for these files.';
  return '';
};

const uploadFiles = async (api) => {
  const form = new FormData();
  form.append('multiple', 'true');
  files.value.forEach((f) => form.append('file[]', f));

  const { data } = await axios.post(`${api}/media-items`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => { if (e.total) progress.value = Math.round((e.loaded / e.total) * 100); },
  });
  return data?.data?.media_ids ?? [];
};

const finish = (ids) => {
  emit('uploaded', ids);
  emit('close');
};

// The audience could not be set. A file meant for some people must not sit
// there open to everyone, so the upload is undone; only if that fails too
// does the modal admit the files exist and who can open them.
const undoUpload = async (api, ids) => {
  try {
    await axios.post(`${api}/media-items/delete`, {
      resources: ids.map((id) => ({ media_id: id, session_id: null })),
      type: 'global_resources',
    });
    busy.value = false;
    error.value = "Couldn't set who can open them, so the files were not added. Try again.";
  } catch (e) {
    console.error(e);
    busy.value = false;
    notice.value = 'The files were added and everyone can open them. Set who can see them from the list.';
    emit('uploaded', ids);
  }
};

const submit = async () => {
  if (!files.value.length || busy.value) return;

  const problem = problemWithFiles();
  if (problem) {
    error.value = problem;
    return;
  }

  busy.value = true;
  error.value = '';
  notice.value = '';
  progress.value = 0;

  const api = window.store.event_backend_api;
  let ids = [];
  try {
    ids = await uploadFiles(api);
  } catch (e) {
    console.error(e);
    error.value = messageFrom(e, 'The upload failed. Try again.');
    busy.value = false;
    return;
  }

  if (isEveryone(access.value) || !ids.length) {
    finish(ids);
    return;
  }

  try {
    await axios.put(`${api}/content-access/media/bulk`, { ids, ...access.value });
    finish(ids);
    return;
  } catch (e) {
    console.error(e);
  }

  await undoUpload(api, ids);
};
</script>

<template>
  <XlModal title="Add files" subtitle="PDF, DOC, PPT, JPG, PNG, GIF, TXT · max 100 MB per upload" max-width="600px" @close="emit('close')">
    <label
      class="upload-files__zone"
      :class="{ 'is-dragging': dragging }"
      @dragover.prevent="dragging = true"
      @dragleave="dragging = false"
      @drop.prevent="onDrop"
    >
      <input type="file" multiple :accept="ACCEPT" class="upload-files__input" :disabled="busy" @change="onPick">
      <b>Choose files</b>
      <span>or drop them here</span>
    </label>

    <ul v-if="files.length" class="upload-files__list">
      <li v-for="(file, i) in files" :key="`${file.name}-${file.size}`">
        <span class="upload-files__name">{{ file.name }}</span>
        <span class="upload-files__size">{{ formatBytes(file.size) }}</span>
        <button type="button" class="upload-files__remove" :aria-label="`Remove ${file.name}`" :disabled="busy" @click="remove(i)">✕</button>
      </li>
    </ul>

    <div class="upload-files__audience">
      <span>Who can open these: <b>{{ summary }}</b></span>
      <XlButton variant="ghost" size="sm" :disabled="busy" @click="pickingAudience = true">Change</XlButton>
    </div>

    <div v-if="busy" class="upload-files__progress" role="progressbar" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100">
      <i :style="{ width: `${progress}%` }" />
    </div>

    <p v-if="error" class="upload-files__error" role="alert">{{ error }}</p>
    <p v-if="notice" class="upload-files__notice" role="status">{{ notice }}</p>

    <template #actions>
      <XlButton variant="ghost" size="sm" @click="emit('close')">{{ notice ? 'Done' : 'Cancel' }}</XlButton>
      <XlButton size="sm" :disabled="!files.length || busy || !!notice" @click="submit">
        Add {{ files.length || '' }} {{ files.length === 1 ? 'file' : 'files' }}
      </XlButton>
    </template>

    <ManageAccessModal
      v-if="pickingAudience"
      :resource="{ name: stagedName, access }"
      :members="members"
      :book="bookFor(null, authStore.userData)"
      subject-type="media"
      @close="pickingAudience = false"
      @saved="onAudience"
    />
  </XlModal>
</template>

<style scoped>
.upload-files__zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 26px 16px;
  border: 1.5px dashed var(--xl-color-outline, #cac4d0);
  border-radius: var(--xl-radius-lg, 16px);
  cursor: pointer;
  text-align: center;
  color: var(--xl-color-secondary-text, #49454f);
}

.upload-files__zone b { color: var(--xl-color-primary, #5e2f78); }
.upload-files__zone.is-dragging { border-color: var(--xl-color-primary, #5e2f78); background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 6%, white); }
.upload-files__input { position: absolute; width: 1px; height: 1px; opacity: 0; overflow: hidden; }

.upload-files__list {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.upload-files__list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
  border-radius: var(--xl-radius-sm, 8px);
  font-size: var(--xl-type-body-medium-size, 14px);
}

.upload-files__name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.upload-files__size { color: var(--xl-color-secondary-text, #49454f); white-space: nowrap; }
.upload-files__remove { border: 0; background: transparent; cursor: pointer; color: var(--xl-color-secondary-text, #49454f); }

.upload-files__audience {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--xl-color-outline-variant, #e7e0ec);
  font-size: var(--xl-type-body-medium-size, 14px);
}

.upload-files__progress { margin-top: 14px; height: 6px; border-radius: 99px; background: var(--xl-color-outline-variant, #e7e0ec); overflow: hidden; }
.upload-files__progress i { display: block; height: 100%; background: var(--xl-color-primary, #5e2f78); transition: width 120ms linear; }

.upload-files__error { margin: 12px 0 0; color: var(--xl-color-error, #ba1a1a); font-size: var(--xl-type-body-medium-size, 14px); }
.upload-files__notice { margin: 12px 0 0; color: var(--xl-color-secondary-text, #49454f); font-size: var(--xl-type-body-medium-size, 14px); }
</style>
